import * as React from 'react'
import clsx from 'clsx'
import { sx as sxFunc, SxProps } from '../sx'
import { isHostComponent, SlotComponentProps, useSlotProps } from '../utils'
import { Mark } from './useSlider.type'
import { SliderValueLabel } from './SliderValueLabel'
import { SliderClasses } from './sliderClasses'
import { Identity, useSlider } from './useSlider'
import styles from './index.module.scss'
import { capitalize } from '../../utils'
import { valueToPercent } from './utils'

export interface SliderComponentsPropsOverrides {}

export interface SliderOwnerState extends SliderOwnProps {}

export interface SliderOwnProps {
  /**
   * The label of the slider.
   */
  'aria-label'?: string
  /**
   * The id of the element containing a label for the slider.
   */
  'aria-labelledby'?: string
  /**
   * A string value that provides a user-friendly name for the current value of the slider.
   */
  'aria-valuetext'?: string
  /**
   * The color of the component.
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'warning' | 'error' | 'info' | 'success'
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<SliderClasses>
  /**
   * @ignore
   */
  className?: string
  /**
   * The default value. Use when the component is not controlled.
   */
  defaultValue?: number | number[]
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean
  /**
   * If `true`, the active thumb doesn't swap when moving pointer over a thumb while dragging another thumb.
   * @default false
   */
  disableSwap?: boolean
  /**
   * Accepts a function which returns a string value that provides a user-friendly name for the thumb labels of the slider.
   * This is important for screen reader users.
   * @param {number} index The thumb label's index to format.
   * @returns {string}
   */
  getAriaLabel?: (index: number) => string
  /**
   * Accepts a function which returns a string value that provides a user-friendly name for the current value of the slider.
   * This is important for screen reader users.
   * @param {number} value The thumb label's value to format.
   * @param {number} index The thumb label's index to format.
   * @returns {string}
   */
  getAriaValueText?: (value: number, index: number) => string
  /**
   * Marks indicate predetermined values to which the user can move the slider.
   * If `true` the marks are spaced according the value of the `step` prop.
   * If an array, it should contain objects with `value` and an optional `label` keys.
   * @default false
   */
  marks?: boolean | Mark[]
  /**
   * The maximum allowed value of the slider.
   * Should not be equal to min.
   * @default 100
   */
  max?: number
  /**
   * The minimum allowed value of the slider.
   * Should not be equal to max.
   * @default 0
   */
  min?: number
  /**
   * Name attribute of the hidden `input` element.
   */
  name?: string
  /**
   * Callback function that is fired when the slider's value changed.
   *
   * @param {Event} event The event source of the callback.
   * You can pull out the new value by accessing `event.target.value` (any).
   * **Warning**: This is a generic event not a change event.
   * @param {number | number[]} value The new value.
   * @param {number} activeThumb Index of the currently moved thumb.
   */
  onChange?: (
    event: Event,
    value: number | number[],
    activeThumb: number
  ) => void
  /**
   * Callback function that is fired when the `mouseup` is triggered.
   *
   * @param {React.SyntheticEvent | Event} event The event source of the callback. **Warning**: This is a generic event not a change event.
   * @param {number | number[]} value The new value.
   */
  onChangeCommitted?: (
    event: React.SyntheticEvent | Event,
    value: number | number[]
  ) => void
  /**
   * The component orientation.
   * @default 'horizontal'
   */
  orientation?: 'horizontal' | 'vertical'
  /**
   * A transformation function, to change the scale of the slider.
   * @param {any} x
   * @returns {any}
   * @default function Identity(x) {
   *   return x;
   * }
   */
  scale?: (value: number) => number
  /**
   * The size of the slider.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * The props used for each slot inside the Slider.
   * @default {}
   */
  slotProps?: {
    root?: SlotComponentProps<
      'span',
      SliderComponentsPropsOverrides,
      SliderOwnerState
    >
    track?: SlotComponentProps<
      'span',
      SliderComponentsPropsOverrides,
      SliderOwnerState
    >
    rail?: SlotComponentProps<
      'span',
      SliderComponentsPropsOverrides,
      SliderOwnerState
    >
    thumb?: SlotComponentProps<
      'span',
      SliderComponentsPropsOverrides,
      SliderOwnerState
    >
    mark?: SlotComponentProps<
      'span',
      SliderComponentsPropsOverrides,
      SliderOwnerState
    >
    markLabel?: SlotComponentProps<
      'span',
      SliderComponentsPropsOverrides,
      SliderOwnerState
    >
    valueLabel?: SlotComponentProps<
      typeof SliderValueLabel,
      SliderComponentsPropsOverrides,
      SliderOwnerState
    >
    input?: SlotComponentProps<
      'input',
      SliderComponentsPropsOverrides,
      SliderOwnerState
    >
  }
  /**
   * The components used for each slot inside the Slider.
   * Either a string to use a HTML element or a component.
   * @default {}
   */
  slots?: {
    root?: React.ElementType
    track?: React.ElementType
    rail?: React.ElementType
    thumb?: React.ElementType
    mark?: React.ElementType
    markLabel?: React.ElementType
    valueLabel?: React.ElementType
    input?: React.ElementType
  }
  /**
   * The granularity with which the slider can step through values. (A "discrete" slider.)
   * The `min` prop serves as the origin for the valid values.
   * We recommend (max - min) to be evenly divisible by the step.
   *
   * When step is `null`, the thumb can only be slid onto marks provided with the `marks` prop.
   * @default 1
   */
  step?: number | null
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * Tab index attribute of the hidden `input` element.
   */
  tabIndex?: number
  /**
   * The track presentation:
   *
   * - `normal` the track will render a bar representing the slider value.
   * - `inverted` the track will render a bar representing the remaining slider value.
   * - `false` the track will render without a bar.
   * @default 'normal'
   */
  track?: 'normal' | false | 'inverted'
  /**
   * The value of the slider.
   * For ranged sliders, provide an array with two values.
   */
  value?: number | number[]
  /**
   * Controls when the value label is displayed:
   *
   * - `auto` the value label will display when the thumb is hovered or focused.
   * - `on` will display persistently.
   * - `off` will never display.
   * @default 'off'
   */
  valueLabelDisplay?: 'on' | 'auto' | 'off'
  /**
   * The format function the value label's value.
   *
   * When a function is provided, it should have the following signature:
   *
   * - {number} value The value label's value to format
   * - {number} index The value label's index to format
   * @param {any} x
   * @returns {any}
   * @default function Identity(x) {
   *   return x;
   * }
   */
  valueLabelFormat?:
    | string
    | ((value: number, index: number) => React.ReactNode)
}

const Forward = ({ children }: any) => {
  return children
}

export const Slider = React.forwardRef<HTMLSpanElement, SliderOwnProps>(
  (props, ref) => {
    const {
      'aria-label': ariaLabel,
      'aria-valuetext': ariaValuetext,
      'aria-labelledby': ariaLabelledby,
      color = 'primary',
      classes,
      className,
      disableSwap = false,
      disabled = false,
      getAriaLabel,
      getAriaValueText,
      marks: marksProp = false,
      max = 100,
      min = 0,
      orientation = 'horizontal',
      size = 'medium',
      step = 1,
      scale = Identity,
      slotProps,
      slots,
      track = 'normal',
      valueLabelDisplay = 'off',
      valueLabelFormat = Identity,
      sx,
    } = props

    const isRtl = false

    const ownerState = {
      ...props,
      isRtl,
      max,
      min,
      classes: classes,
      disabled,
      disableSwap,
      orientation,
      marks: marksProp,
      color,
      size,
      step,
      scale,
      track,
      valueLabelDisplay,
      valueLabelFormat,
    }

    const {
      axisProps,
      getRootProps,
      getHiddenInputProps,
      getThumbProps,
      open,
      active,
      axis,
      focusedThumbIndex,
      range,
      dragging,
      marks,
      values,
      trackOffset,
      trackLeap,
      getThumbStyle,
    } = useSlider({
      ...ownerState,
      rootRef: ref,
    })

    const myOwnerState = {
      ...ownerState,
      marked: marks.length > 0 && marks.some(mark => mark.label),
      dragging,
      focusedThumbIndex,
    }

    const RootSlot = slots?.root ?? 'span'
    const RailSlot = slots?.rail ?? 'span'
    const TrackSlot = slots?.track ?? 'span'
    const ThumbSlot = slots?.thumb ?? 'span'
    const ValueLabelSlot = slots?.valueLabel ?? SliderValueLabel
    const MarkSlot = slots?.mark ?? 'span'
    const MarkLabelSlot = slots?.markLabel ?? 'span'
    const InputSlot = slots?.input ?? 'input'

    const { ownerState: _, ...rootProps } = useSlotProps({
      elementType: RootSlot,
      getSlotProps: getRootProps,
      externalSlotProps: slotProps?.root,
      ownerState: {},
      className: clsx(
        classes?.root,
        styles.root,
        styles[`orientation-${orientation}`],
        styles[`color--${color}`],
        // @ts-ignore
        classes?.[`color${capitalize(color)}`],
        styles[`size--${size}`],
        // @ts-ignore
        classes?.[`size${capitalize(size)}`],
        myOwnerState.marked && [styles.marked, classes?.marked],
        disabled && [styles.disabled, classes?.disabled],
        className,
        sxFunc(sx)
      ),
    })

    const { ownerState: __, ...railProps } = useSlotProps({
      elementType: RailSlot,
      externalSlotProps: slotProps?.rail,
      ownerState: {},
      className: clsx(
        classes?.rail,
        styles.rail,
        styles[`orientation-${orientation}`],
        styles[`track-${track}`],
        track === 'inverted' && classes?.trackInverted,
        className
      ),
    })

    const { ownerState: ___, ...trackProps } = useSlotProps({
      elementType: TrackSlot,
      externalSlotProps: slotProps?.track,
      ownerState: {},
      additionalProps: {
        style: {
          ...axisProps[axis].offset(trackOffset),
          ...axisProps[axis].leap(trackLeap),
        },
      },
      className: clsx(
        classes?.track,
        styles.track,
        styles[`orientation-${orientation}`],
        styles[`size--${size}`],
        // @ts-ignore
        classes?.[`size${capitalize(size)}`],
        styles[`track-${track}`],
        track === false && classes?.trackFalse,
        track === 'inverted' && classes?.trackInverted,
        dragging === true && [styles?.dragging, classes?.dragging]
      ),
    })

    const { ownerState: ____, ...markProps } = useSlotProps({
      elementType: MarkSlot,
      externalSlotProps: slotProps?.mark,
      ownerState: {},
      className: clsx(
        classes?.mark,
        styles.sliderMark,
        styles[`orientation-${orientation}`]
      ),
    })

    const { ownerState: _____, ...markLabelProps } = useSlotProps({
      elementType: MarkLabelSlot,
      externalSlotProps: slotProps?.markLabel,
      ownerState: {},
      className: clsx(
        classes?.markLabel,
        styles.sliderMarkLabel,
        styles[`orientation-${orientation}`]
      ),
    })

    const { ownerState: ________, ...valueLabelProps } = useSlotProps({
      elementType: ValueLabelSlot,
      externalSlotProps: slotProps?.valueLabel,
      ownerState: {},
      className: clsx(
        classes?.valueLabel,
        styles.sliderValueLabel,
        styles[`orientation-${orientation}`],
        styles[`size--${size}`]
      ),
    })

    const { ownerState: ______, ...thumbProps } = useSlotProps({
      elementType: ThumbSlot,
      getSlotProps: getThumbProps,
      externalSlotProps: slotProps?.thumb,
      ownerState: {},
      className: clsx(
        classes?.thumb,
        styles.thumb,
        styles[`orientation-${orientation}`],
        styles[`size--${size}`],
        dragging === true && [styles?.dragging, classes?.dragging],
        disabled && [styles.disabled, classes?.disabled]
      ),
    })

    const { ownerState: _________, ...inputSliderProps } = useSlotProps({
      elementType: InputSlot,
      getSlotProps: getHiddenInputProps,
      externalSlotProps: slotProps?.input,
      ownerState: {},
    })

    return (
      <RootSlot {...rootProps}>
        <RailSlot {...railProps} />
        <TrackSlot {...trackProps} />
        {marks
          .filter(mark => mark.value >= min && mark.value <= max)
          .map((mark, index, marksArray) => {
            const percent = valueToPercent(mark.value, min, max)
            const markstyle = axisProps[axis].offset(percent)

            let markActive
            if (track === false) {
              markActive = values.indexOf(mark.value) !== -1
            } else {
              markActive =
                (track === 'normal' &&
                  (range
                    ? mark.value >= values[0]! &&
                      mark.value <= values[values.length - 1]!
                    : mark.value <= values[0]!)) ||
                (track === 'inverted' &&
                  (range
                    ? mark.value <= values[0]! ||
                      mark.value >= values[values.length - 1]!
                    : mark.value >= values[0]!))
            }
            return (
              <React.Fragment key={index}>
                <MarkSlot
                  data-index={index}
                  {...markProps}
                  className={clsx(
                    markProps.className,
                    markActive && styles.markActive,
                    index === 0 && styles.firstMark,
                    index === marksArray.length - 1 && styles.lastMark
                  )}
                  style={{ ...markProps.style, ...markstyle }}
                />
                {mark.label != null ? (
                  <MarkLabelSlot
                    aria-hidden
                    data-index={index}
                    {...markLabelProps}
                    className={clsx(
                      markLabelProps.className,
                      markActive && styles.markActive
                    )}
                    style={{ ...markLabelProps.style, ...markstyle }}
                  >
                    {mark.label}
                  </MarkLabelSlot>
                ) : null}
              </React.Fragment>
            )
          })}
        {values.map((value, index) => {
          const percent = valueToPercent(value, min, max)
          const valueStyle = axisProps[axis].offset(percent)

          const ValueLabelComponent =
            valueLabelDisplay === 'off' ? Forward : ValueLabelSlot

          return (
            <ValueLabelComponent
              key={index}
              {...(!isHostComponent(ValueLabelComponent) && {
                valueLabelFormat,
                valueLabelDisplay,
                value:
                  typeof valueLabelFormat === 'function'
                    ? valueLabelFormat(scale(value), index)
                    : valueLabelFormat,
                index,
                open:
                  open === index ||
                  active === index ||
                  valueLabelDisplay === 'on',
                disabled,
                classes: {
                  valueLabelOpen: clsx(
                    styles.valueLabelOpen,
                    classes?.valueLabelOpen
                  ),
                  valueLabelCircle: classes?.valueLabelCircle,
                  valueLabelLabel: classes?.valueLabelLabel,
                },
              })}
              {...valueLabelProps}
            >
              <ThumbSlot
                data-index={index}
                {...thumbProps}
                className={clsx(
                  classes?.thumb,
                  thumbProps.className,
                  active === index && [classes?.active, styles.active],
                  focusedThumbIndex === index && [
                    classes?.focusVisible,
                    styles.focusVisible,
                  ]
                )}
                style={{
                  ...valueStyle,
                  ...getThumbStyle(index),
                  ...thumbProps.style,
                }}
              >
                <InputSlot
                  data-index={index}
                  aria-label={getAriaLabel ? getAriaLabel(index) : ariaLabel}
                  aria-valuenow={scale(value)}
                  aria-labelledby={ariaLabelledby}
                  aria-valuetext={
                    getAriaValueText
                      ? getAriaValueText(scale(value), index)
                      : ariaValuetext
                  }
                  value={values[index]}
                  {...inputSliderProps}
                />
              </ThumbSlot>
            </ValueLabelComponent>
          )
        })}
      </RootSlot>
    )
  }
)

Slider.displayName = 'Slider'
